import { useMemo } from 'react';
import { Box, Stack, Button, Alert, CircularProgress } from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import { useMarkPotPaid } from '../../hooks/useMarkPotPaid';
import { PotSummaryCard } from '../PotSummaryCard';
import {
  PaymentBreakdownCard,
  type PaymentLineItem,
} from '../PaymentBreakdownCard';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface MarkPotPaidPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onSuccess: () => void;
}

export function MarkPotPaidPanelContent({
  potDetails,
  onSuccess,
}: MarkPotPaidPanelContentProps) {
  const { mutate: markPotPaid, isPending } = useMarkPotPaid();

  const handleConfirmPayment = () => {
    markPotPaid(
      {
        potPeriodId: potDetails.potPeriodId,
        potId: potDetails.id,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  // Build payment line items from gang member splits, fixed rate workers, or solo owner
  const paymentLineItems = useMemo((): PaymentLineItem[] => {
    if (potDetails.owner.ownerType === 'Solo') {
      // Solo pot: single line item for the owner
      return [
        {
          id: 'owner',
          name: potDetails.owner.ownerName,
          amount: potDetails.financials?.netAmount ?? 0,
          type: 'solo',
        },
      ];
    }

    // Gang pot: combine gang member splits and fixed rate workers
    const items: PaymentLineItem[] = [];

    // Add gang member splits (only those with non-zero amounts)
    // Fixed rate workers have £0 split and are shown separately
    potDetails.gangMemberSplits
      .filter(member => member.amount > 0)
      .forEach(member => {
        items.push({
          id: `split-${member.userId}`,
          name: member.userName,
          amount: member.amount,
          type: 'split',
        });
      });

    // Add fixed rate workers
    potDetails.fixedRateWorkers.forEach(worker => {
      items.push({
        id: `dayrate-${worker.userId}`,
        name: worker.userName,
        amount: worker.totalAmount,
        type: 'dayRate',
      });
    });

    return items;
  }, [potDetails]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Stack spacing={3}>
          <PotSummaryCard
            ownerType={potDetails.owner.ownerType}
            ownerName={potDetails.owner.ownerName}
            period={potDetails.period}
          />

          <PaymentBreakdownCard items={paymentLineItems} />
        </Stack>
      </Box>

      {/* Fixed bottom action area */}
      <Box
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Helper text */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Marking this pot as paid confirms the payment has been processed. This
          action cannot be undone.
        </Alert>

        <Button
          variant="contained"
          color="success"
          size="large"
          fullWidth
          startIcon={
            isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <PaidIcon />
            )
          }
          onClick={handleConfirmPayment}
          disabled={isPending}
          data-testid="confirm-payment-button"
        >
          {isPending ? 'Processing...' : 'Confirm Payment'}
        </Button>
      </Box>
    </Box>
  );
}
